// Berechnungszeile: Wahl je Spalte (je Nutzer und Liste gemerkt) und Ergebnisse vom Server (Agent 10B, Runde 10).
// Der Server rechnet über alle Treffer der Liste. Gerechnet wird nur, wenn mindestens eine Spalte eine Rechenart hat; ändern sich Quelle,
// Wahl oder `stempel` (die Seite zählt ihn bei jedem Neuladen der Liste hoch), kommt nach kurzer Pause eine neue Anfrage. Bis dahin bleiben
// die alten Werte stehen (gedimmt), damit die Zeile nicht flackert.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fehlerText } from "../../api/client";
import { listenBerechnungApi, type BerechnungsZiel } from "../../api/listenBerechnung";
import type { AnsichtZugriff } from "../../daten/listenAnsichtStore";
import { beimNutzerwechsel } from "../../daten/sitzung";
import { wahlAendern, wahlLesen, type BerechnungsErgebnis, type BerechnungsFunktion, type BerechnungsWahl } from "./berechnungModell";

export const EINSTELLUNG_BERECHNUNG = "berechnung";
const PAUSE_MS = 220;

let sitzung = 0;
beimNutzerwechsel(() => {
  sitzung++;
});

interface Optionen {
  gemerkt: AnsichtZugriff;
  /** Typ der Spalte im Katalog der Lead-Listen; `undefined`, wenn sich die Spalte nicht berechnen lässt */
  typVon: (key: string) => string | undefined;
  /** Schlüssel der Spalten, die gerade in der Liste stehen */
  gezeigt: string[];
  /** Quelle der Treffer; `null`, solange die Liste noch nichts hat */
  ziel: BerechnungsZiel | null;
  /** ändert sich, wenn die Liste neu geladen wurde (Werte können sich geändert haben) */
  stempel: string | number;
}

export interface ListenBerechnung {
  wahl: BerechnungsWahl;
  ergebnisse: Record<string, BerechnungsErgebnis>;
  laedt: boolean;
  fehler: string | null;
  /** Leads, über die gerechnet wurde, und ob der Server bei seiner Obergrenze gekappt hat */
  anzahl: number | null;
  gekappt: boolean;
  setzen: (key: string, funktion: BerechnungsFunktion | null) => void;
  alleEntfernen: () => void;
}

export function useListenBerechnung({ gemerkt, typVon, gezeigt, ziel, stempel }: Optionen): ListenBerechnung {
  const roh = gemerkt.ansicht?.einstellungen?.[EINSTELLUNG_BERECHNUNG];
  // alles Gemerkte, ob die Spalte gerade da ist oder nicht (eine ausgeblendete Spalte behält ihre Rechenart)
  const gemerktAlle = useMemo(() => wahlLesen(roh, typVon), [roh, typVon]);
  const gezeigtMenge = useMemo(() => new Set(gezeigt), [gezeigt]);
  const wahl = useMemo(() => {
    const aus: BerechnungsWahl = {};
    for (const [k, f] of Object.entries(gemerktAlle)) if (gezeigtMenge.has(k)) aus[k] = f;
    return aus;
  }, [gemerktAlle, gezeigtMenge]);

  const [stand, setStand] = useState<{ ergebnisse: Record<string, BerechnungsErgebnis>; anzahl: number | null; gekappt: boolean }>({ ergebnisse: {}, anzahl: null, gekappt: false });
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const zaehler = useRef(0);

  const schluessel = useMemo(() => (ziel && Object.keys(wahl).length ? JSON.stringify([ziel, wahl, stempel]) : null), [ziel, wahl, stempel]);
  const zielRef = useRef(ziel);
  zielRef.current = ziel;
  const wahlRef = useRef(wahl);
  wahlRef.current = wahl;

  useEffect(() => {
    if (!schluessel) {
      zaehler.current++;
      setLaedt(false);
      setFehler(null);
      setStand((s) => (Object.keys(s.ergebnisse).length ? { ergebnisse: {}, anzahl: null, gekappt: false } : s));
      return;
    }
    const meine = ++zaehler.current;
    const meineSitzung = sitzung;
    setLaedt(true);
    const t = window.setTimeout(() => {
      const z = zielRef.current;
      if (!z) return;
      listenBerechnungApi
        .leads(z, wahlRef.current)
        .then((r) => {
          if (meine !== zaehler.current || meineSitzung !== sitzung) return;
          setStand({ ergebnisse: r.ergebnisse, anzahl: r.anzahl, gekappt: r.gekappt });
          setFehler(null);
          setLaedt(false);
        })
        .catch((e: unknown) => {
          if (meine !== zaehler.current || meineSitzung !== sitzung) return;
          setFehler(fehlerText(e));
          setLaedt(false);
        });
    }, PAUSE_MS);
    return () => window.clearTimeout(t);
  }, [schluessel]);

  const setzen = useCallback(
    (key: string, funktion: BerechnungsFunktion | null) => {
      gemerkt.aendern({ einstellungen: { [EINSTELLUNG_BERECHNUNG]: wahlAendern(gemerktAlle, key, funktion) } });
    },
    [gemerkt, gemerktAlle],
  );
  const alleEntfernen = useCallback(() => gemerkt.aendern({ einstellungen: { [EINSTELLUNG_BERECHNUNG]: null } }), [gemerkt]);

  return { wahl, ergebnisse: stand.ergebnisse, laedt, fehler, anzahl: stand.anzahl, gekappt: stand.gekappt, setzen, alleEntfernen };
}
